import { useMultiARCs } from "providers/MultiARCsControlProvider";
import { typeOfPoint, useSelectPoint, useSelectedRobots } from "../../FleetPage";
import FluidListView from "components/layout/FluidListView";
import ARCCard from "components/ARC/ARCCard";
import Card from "components/common/Card";
import { Landmark } from "arcjs/common/arc_devices";
import FunctionalButton from "components/layout/FunctionalButton";
import { useCallback, useEffect, useMemo } from "react";
import { Point2D } from "arcjs/common/geometry";
import isPlainObject from "lodash/isPlainObject";
import SubmitButton from "components/layout/SubmitButton";
import CancelButton from "components/layout/CancelButton";
import { useFleet } from "providers/FleetProvider";

export type LandmarkCardProps = React.HTMLProps<HTMLDivElement> & {
    point: Landmark;
};

export type CoordinatesCardProps = React.HTMLProps<HTMLDivElement> & {
    point: Point2D;
};

export const LandmarkCard = ({
    point: {
        name,
        pose: {
            position: { x, y },
        },
    },
}: LandmarkCardProps) => {
    return (
        <Card>
            <Card.Title>{name}</Card.Title>
            <Card.Content>
                Coordinates: {x}, {y}
            </Card.Content>
        </Card>
    );
};

export const CoordinatesCard = ({ point: { x, y }, className = "" }: CoordinatesCardProps) => {
    return (
        <Card className={className}>
            <Card.Title>A coordinate</Card.Title>
            <Card.Content>
                {x}, {y}
            </Card.Content>
        </Card>
    );
};

export type NavigateToSelectorProps = {
    onNavigationStarted: (point: Point2D | Landmark) => void;
} & React.HTMLProps<HTMLDivElement>;

const NavigateToSelector = ({ className = "", onNavigationStarted }: NavigateToSelectorProps) => {
    const { selectedRobots } = useSelectedRobots();

    const { arcs } = useMultiARCs();
    const { arcStatuses } = useFleet();

    const { selectedPoint, deselectPoint, setPendingSelectPoint } = useSelectPoint();

    useEffect(() => {
        setPendingSelectPoint(true);
        return () => {
            setPendingSelectPoint(false);
        };
    }, [setPendingSelectPoint]);

    if (selectedRobots.length === 0)
        throw new Error("NavigateToSelector expects at least 1 selectedRobots. Got 0");
    const selectedARCs = selectedRobots.map((serial) => arcs[serial]);

    const moveSelectedARCsToPoint2D = useCallback(
        (point: Point2D) => {
            for (const arc of selectedARCs) {
                console.log("arc", arc.serialNumber, "is to be moved to point", point);
            }
        },
        [selectedARCs]
    );

    const moveSelectedARCsToLandmark = useCallback(
        (landmark: Landmark) => {
            for (const arc of selectedARCs) {
                console.log("arc", arc.serialNumber, "is to be moved to landmark", landmark);
            }
        },
        [selectedARCs]
    );

    const pointType = useMemo(() => {
        return isPlainObject(selectedPoint) ? typeOfPoint(selectedPoint) : null;
    }, [selectedPoint]);

    const moveSelectedARCs = useMemo<(point: Landmark | Point2D) => void>(
        () => (pointType === "Landmark" ? moveSelectedARCsToLandmark : moveSelectedARCsToPoint2D),
        [moveSelectedARCsToLandmark, moveSelectedARCsToPoint2D, pointType]
    );

    const PointViewComponent = useMemo<
        React.JSXElementConstructor<{ point: Landmark | Point2D } & React.HTMLProps<HTMLDivElement>>
    >(() => (pointType === "Landmark" ? LandmarkCard : CoordinatesCard), [pointType]);

    return (
        <div className={`${className} flex h-full flex-col space-y-8`}>
            <div className="text-center text-lg">
                {selectedPoint ? (
                    <>
                        <p className="py-4">You selected a point at:</p>
                        <PointViewComponent point={selectedPoint} className="w-full" />
                    </>
                ) : (
                    <>Select a point on the map</>
                )}
            </div>
            <div className="flex w-full justify-center space-x-4">
                <SubmitButton
                    title="Move"
                    onClick={() => {
                        moveSelectedARCs(selectedPoint);
                        onNavigationStarted(selectedPoint);
                    }}
                    disabled={!selectedPoint}
                />
                <CancelButton
                    title="Clear"
                    onClick={() => {
                        deselectPoint();
                    }}
                    disabled={!selectedPoint}
                />
            </div>
            <h5>Robots to move:</h5>
            <FluidListView className="w-full grow">
                {selectedARCs.map((arc) => (
                    <ARCCard
                        key={arc.serialNumber}
                        arc={arc}
                        arcStatus={arcStatuses[arc.serialNumber]}
                        action={null}
                    />
                ))}
            </FluidListView>
        </div>
    );
};
export default NavigateToSelector;
